import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit } from "@angular/core";
import { CustomFieldActions } from "@gtmhub/customFields/redux/custom-field-actions";
import { ICustomFieldsStoreState } from "@gtmhub/customFields/redux/custom-field-reducer";
import { PlanningSessionsActions } from "@gtmhub/sessions/redux/session-actions";
import { ISessionsStoreState } from "@gtmhub/sessions/redux/session-reducer";
import { filterActiveAndFutureSessions } from "@gtmhub/sessions/redux/session-selectors";
import { reduxStoreContainer } from "@gtmhub/state-management/state-management.module";
import { ReduxStoreObserver } from "@webapp/core/state-management/redux-store-observer";
import { ICustomField } from "@webapp/custom-fields/models/custom-fields.models";
import { SingleGoalSelectorService } from "@webapp/okrs/components/single-goal-selector/single-goal-selector.service";
import { Metric } from "@webapp/okrs/metrics/models/metric.models";
import { Session } from "@webapp/sessions/models/sessions.model";
import { getSessionAndOneLevelChildren } from "@webapp/sessions/utils/utils";
import { UiModalRef } from "@webapp/ui/modal/abstracts/modal-ref";
import { UI_MODAL_DATA } from "@webapp/ui/modal/modal.models";
import { CloneMoveOkrModalService } from "../../../clone-move-okr-modal.service";
import { CloneOrMoveResourceService } from "../../../clone-move-resource.service";
import { ActionType, CloneOrMoveResourceType, IGoalCloneSelected, IMetricCloneSelected } from "../../../clone-or-move.types";

@Component({
  selector: "clone-kr-modal",
  templateUrl: "./clone-kr-modal.component.html",
  styleUrls: ["./clone-kr-modal.component.less"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CloneKrModalComponent implements OnInit {
  public metric: Metric;
  public isDynamicMetric: boolean;
  public selectedModalOptions: IMetricCloneSelected;

  private activeSessions: Session[];
  private metricCustomFields: ICustomField[];

  constructor(
    private modalRef: UiModalRef,
    private cdRef: ChangeDetectorRef,
    private cloneMoveOKRService: CloneMoveOkrModalService,
    private planningSessionsActions: PlanningSessionsActions,
    private customFieldActions: CustomFieldActions,
    private cloneOrMoveResourceService: CloneOrMoveResourceService,
    private singleGoalSelectorService: SingleGoalSelectorService,
    @Inject(UI_MODAL_DATA) modalData: { metric: Metric }
  ) {
    this.selectedModalOptions = {
      session: null,
      goal: null,
      progress: true,
      notifications: false,
      duplicateInsight: false,
      sync: false,
    };

    Object.assign(this, modalData);
  }

  public ngOnInit(): void {
    this.isDynamicMetric = !!(this.metric as Metric)?.insightName;

    const { reduxStore } = reduxStoreContainer;
    reduxStore.dispatch(this.planningSessionsActions.getSessionsIfMissing());
    reduxStore.dispatch(this.customFieldActions.getCustomFields());

    const reduxObserver = new ReduxStoreObserver(reduxStore);
    reduxObserver.whenFetched$<ISessionsStoreState & ICustomFieldsStoreState>("sessions", "customFields").subscribe((state) => {
      this.setStates(state);
    });
  }

  private setStates(state: ISessionsStoreState & ICustomFieldsStoreState): void {
    this.activeSessions = filterActiveAndFutureSessions(state.sessions.items);
    this.metricCustomFields = state.customFields.map["metric"] || [];
  }

  public openGoalSelector(): void {
    this.singleGoalSelectorService.openSingleGoalSelectModal({ enableOkrSelectionOnly: true }).subscribe((selection) => {
      if (!selection || !selection.type || !selection.goal) {
        return;
      }
      const validSessions = getSessionAndOneLevelChildren(this.activeSessions, selection.goal?.sessionId);
      this.selectedModalOptions.goal = selection.goal;

      if (validSessions.length > 0) {
        this.selectedModalOptions.session = validSessions[0];
      }
      this.cdRef.detectChanges();
    });
  }

  public validateSelectedOptions(): void {
    if (this.selectedModalOptions.duplicateInsight) {
      this.selectedModalOptions.sync = false;
    }

    if (this.selectedModalOptions.sync) {
      this.selectedModalOptions.duplicateInsight = false;
    }

    if (!this.selectedModalOptions.progress) {
      this.selectedModalOptions.sync = false;
    }

    this.cdRef.detectChanges();
  }

  public startCloning(): void {
    const customFieldsWithSpecificSessions = this.metricCustomFields
      .filter((customField) => customField.targetIds)
      .filter((customField) => customField.targetIds.includes(this.metric.sessionId) && !customField.targetIds.includes(this.selectedModalOptions.session.id));

    const metricCustomFieldsCount = this.metric.customFields ? Object.keys(this.metric.customFields).length : 0;
    const customFieldsCount = metricCustomFieldsCount && customFieldsWithSpecificSessions.length;

    if (customFieldsCount > 0) {
      this.cloneMoveOKRService.openConfirmCloneOrMoveModal(ActionType.Clone, customFieldsCount, CloneOrMoveResourceType.KeyResult).subscribe((confirmed) => {
        if (confirmed) {
          this.cloneOrMoveResourceService.cloneMetric(this.metric as Metric, this.selectedModalOptions as IGoalCloneSelected);
          this.destroyModal();
        }
      });
    } else {
      this.cloneOrMoveResourceService.cloneMetric(this.metric as Metric, this.selectedModalOptions as IGoalCloneSelected);
      this.destroyModal();
    }
  }

  public destroyModal(): void {
    this.modalRef.destroy();
  }
}
